import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ResourceType } from '../../../../enumerations/ResourceType';
import {
    useMultiParentlessResourceAdapterActions,
} from '../../../../resource-base/adapters/useMultiParentlessResourceAdapterActions';
import { get, patch } from '../../../../resource-base/APIVersion1';
import {
    UpdateSettingMutateObject,
    UpdateSettingMutateObjectSchema,
    UpdateSettingsByCategoryMutateObject,
    UpdateSettingsByCategoryMutateObjectSchema,
} from '../../../../resource-base/mutateObjects';
import {
    defaultPersistableResource,
    PersistableResourceSchema,
} from '../../../../resource-base/persistableResourceTypes';
import { SettingDefinitionMappingSchema } from '../../../../resource-base/settings/reactCommonsSchemas';
import { ParamConstants } from '../../../../resource-base/types';
import { useMutationWithDelay } from '../../../../resource-base/useMutationWithDelay';

export const SystemSettingsResourceSchema = PersistableResourceSchema.extend({
    resourceType: z.literal(ResourceType.SYSTEM_SETTINGS).default(ResourceType.SYSTEM_SETTINGS),
});

export type SystemSettingsResource = z.infer<typeof SystemSettingsResourceSchema>;

export const defaultSystemSettingsResource: SystemSettingsResource = {
    ...defaultPersistableResource,
    resourceType: ResourceType.SYSTEM_SETTINGS,
};

export function useSystemSettingsActions() {
    const resourceType = ResourceType.SYSTEM_SETTINGS;
    const actions = useMultiParentlessResourceAdapterActions(
        'system-settings',
        resourceType,
        SystemSettingsResourceSchema
    );

    const useGetSettingsMapping = () => {
        return useQuery({
            queryKey: ['SYSTEM_SETTINGS_MAPPING'],
            queryFn: () =>
                get(`system-settings/mapping`).then((result) =>
                    actions.processGetResponse(result, { settings: [], categories: [] }, SettingDefinitionMappingSchema)
                ),
        });
    };

    const useUpdateSetting = () => {
        return useMutationWithDelay(
            ({ setting, id, validationDepth }: UpdateSettingMutateObject) =>
                patch(
                    `system-settings/${id}/settings?${ParamConstants.UPDATE_VALIDATION_DEPTH_REQUEST_PARAM_NAME}=${validationDepth}`,
                    JSON.stringify(setting)
                ).then((result) => actions.processMutationResponse(result, 'UPDATE')),
            UpdateSettingMutateObjectSchema
        );
    };

    const useUpdateSettingsByCategory = () => {
        return useMutationWithDelay(
            ({ settings, id, validationDepth, category }: UpdateSettingsByCategoryMutateObject) =>
                patch(
                    `system-settings/${id}/category/${category}/settings?${ParamConstants.UPDATE_VALIDATION_DEPTH_REQUEST_PARAM_NAME}=${validationDepth}`,
                    JSON.stringify(settings)
                ).then((result) => actions.processMutationResponse(result, 'UPDATE')),
            UpdateSettingsByCategoryMutateObjectSchema
        );
    };

    const useGetSingleResource = () => {
        return useQuery({
            queryKey: [resourceType],
            queryFn: () => get(`system-settings`).then((result) => actions.processGetResourceResponse(result)),
        });
    };

    return {
        useAddResource: actions.useAddResource,
        useUpdateResource: actions.useUpdateResource,
        useValidateResource: actions.useValidateResource,
        useGetSingleResource,
        useGetSettingsMapping,
        useUpdateSetting,
        useUpdateSettingsByCategory,
    };
}
